// Form component for creating Correspondence Notes for Tickets

import { useState } from "react";
import { Box, Button, Stack } from "@mui/material";

import { OutlinedInput } from "@components/outlined-input";
import { OutlinedSelect } from "@components/outlined-select";
import { useCorrespondenceContext } from "@hooks/use-correspondence-context";

type CorrespondenceFormProps = {
  ticketId: number;
  refreshCorrespondenceList: () => void;
  setVisibility: (value: string) => void;
  setAlertStatus: (val: string) => void;
};

type FormState = {
  recipient_email?: string;
  visibility: string;
  note_text: string;
};

export const CorrespondenceForm = ({
  ticketId,
  refreshCorrespondenceList,
  setVisibility,
  setAlertStatus,
}: CorrespondenceFormProps) => {
  const { createCorrespondenceNote } = useCorrespondenceContext();

  const [formState, setFormState] = useState<FormState>({
    visibility: "internal",
    note_text: "",
  });
  const [errMsg, setErrMsg] = useState<FormState>({
    recipient_email: "",
    visibility: "",
    note_text: "",
  });

  const validateSubmit = (submit: FormState) => {
    let isValid: boolean = true;
    let newErrMsg: FormState = {
      recipient_email: "",
      visibility: "",
      note_text: "",
    };

    if (submit.visibility === "public") {
      const emailStr: string = submit.recipient_email ?? "";
      const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (emailStr === "") {
        newErrMsg.recipient_email =
          "Outgoing email is required for public notes";
        isValid = false;
      } else if (
        !emailStr.includes("@") ||
        !emailStr.includes(".") ||
        !emailRegex.test(emailStr)
      ) {
        newErrMsg.recipient_email = "Please enter a valid email address";
        isValid = false;
      }
    }

    if (submit.note_text === "") {
      newErrMsg.note_text = "The Note body cannot be empty.";
      isValid = false;
    }

    setErrMsg(newErrMsg);
    return isValid;
  };

  const handleSubmit = async (submit: FormState) => {
    if (!validateSubmit(submit)) return;

    let formattedSubmit: Record<string, any> = {
      visibility: submit.visibility,
      note_text: submit.note_text,
    };

    submit.recipient_email &&
      (formattedSubmit.recipient_email = submit.recipient_email);

    const res = await createCorrespondenceNote(ticketId, formattedSubmit);

    if (res.success === true) {
      setFormState({
        visibility: "internal",
        note_text: "",
      });
      setVisibility(submit.visibility);
      setAlertStatus(res.sent ? "success" : "error");
      refreshCorrespondenceList();
    }
  };

  return (
    <Box component="form">
      <OutlinedSelect
        label="Visibility"
        name="visibility"
        value={formState.visibility}
        onChange={(val) =>
          setFormState((prev: FormState) => ({ ...prev, visibility: val }))
        }
        options={[
          { label: "Public", value: "public" },
          { label: "Internal", value: "internal" },
        ]}
        sx={{
          mb: 2,
        }}
      />

      {formState.visibility === "public" && (
        <OutlinedInput
          label="Send To"
          name="recipient_email"
          value={formState.recipient_email}
          onChange={(value: string) => {
            setFormState((prev: FormState) => ({
              ...prev,
              recipient_email: value,
            }));
          }}
          type="email"
          error={errMsg.recipient_email}
          sx={{
            mb: 2,
          }}
        />
      )}

      <OutlinedInput
        label="Note"
        name="note_text"
        value={formState.note_text}
        onChange={(value: string) => {
          setFormState((prev: FormState) => ({ ...prev, note_text: value }));
        }}
        multiline
        error={errMsg.note_text}
        sx={{
          mb: 2,
        }}
      />

      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit(formState)}
          sx={{
            borderRadius: 999,
            px: 3,
            py: 0.75,
          }}
        >
          Submit
        </Button>
      </Stack>
    </Box>
  );
};
